import { useState, useRef, useEffect, type ReactNode } from 'react'
import { ChevronDownIcon, CheckIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { OnboardingLabel } from './OnboardingLabel'

export interface SelectOption<T = string> {
  value: T
  label: string
  /** Secondary text shown after the label */
  sublabel?: string
  /** Icon or emoji shown before the label */
  icon?: ReactNode
  /** Whether this is a default option (shows "Default" badge) */
  isDefault?: boolean
}

interface OnboardingSelectProps<T = string> {
  /** Current selected value */
  value: T
  /** Callback when selection changes */
  onChange: (value: T) => void
  /** Label text for the select */
  label?: string
  /** Available options */
  options: SelectOption<T>[]
  /** Placeholder text when no value selected */
  placeholder?: string
  /** Show "Default" badge for default options */
  showDefaultBadge?: boolean
  /** Error message */
  error?: string
}

export function OnboardingSelect<T = string>({
  value,
  onChange,
  label,
  options,
  placeholder = 'Select an option',
  showDefaultBadge = true,
  error,
}: OnboardingSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((opt) => opt.value === value)

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div ref={containerRef}>
      {label && <OnboardingLabel>{label}</OnboardingLabel>}
      <div className="relative">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full cursor-pointer items-center gap-2 rounded-lg border bg-white px-3.5 py-2.5 text-left transition-colors',
            isOpen
              ? 'border-[#151515]'
              : error
                ? 'border-[#f04438]'
                : 'border-[#d5d7da] hover:border-[#a4a7ae]'
          )}
          style={{
            boxShadow: isOpen
              ? 'inset 0 0 0 1px #151515, 0px 1px 2px 0px rgba(10,13,18,0.05)'
              : '0px 1px 2px 0px rgba(10,13,18,0.05)',
          }}
        >
          {selectedOption ? (
            <>
              {selectedOption.icon && (
                <span className="text-xl leading-none">{selectedOption.icon}</span>
              )}
              <span className="flex flex-1 items-center gap-2">
                <span className="font-medium text-[#181d27]">{selectedOption.label}</span>
                <span className="text-[#535862]">
                  {showDefaultBadge && selectedOption.isDefault
                    ? 'Default'
                    : selectedOption.sublabel}
                </span>
              </span>
            </>
          ) : (
            <span className="flex-1 text-[#717680]">{placeholder}</span>
          )}
          <ChevronDownIcon
            className={cn(
              'h-5 w-5 text-[#a4a7ae] transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute top-full left-0 z-50 mt-1 w-full overflow-hidden rounded-lg border border-[rgba(0,0,0,0.08)] bg-white py-1 shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03)]">
            <div className="max-h-[320px] overflow-auto">
              {options.map((option, index) => {
                const isSelected = option.value === value
                return (
                  <button
                    key={String(option.value) + index}
                    type="button"
                    onClick={() => {
                      onChange(option.value)
                      setIsOpen(false)
                    }}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left transition-colors',
                      isSelected ? 'bg-[#fafafa]' : 'hover:bg-[#fafafa]'
                    )}
                  >
                    {option.icon && (
                      <span className="text-xl leading-none">{option.icon}</span>
                    )}
                    <span className="flex flex-1 items-center gap-2">
                      <span className="font-medium text-[#181d27]">{option.label}</span>
                      <span className="text-[#535862]">{option.sublabel}</span>
                    </span>
                    {isSelected && <CheckIcon className="h-5 w-5 text-[#151515]" />}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-[#f04438]">{error}</p>}
    </div>
  )
}
